import * as React from "react";
import { useState } from "react";
import { Button, Callout, Flex, Text, TextField } from "@radix-ui/themes";
import { useAuthActions } from "@convex-dev/auth/react";
import { useSignInWithApple } from "@convex-dev/auth/methods/apple/react";
import { useSignInWithGoogle } from "@convex-dev/auth/methods/google/react";
import { useOauth, type OauthFlowError } from "@convex-dev/auth/methods/oauth/react";
import { useMutation } from "convex/react";
import { api } from "@repo/convex/convex/_generated/api";

// Sign in with Apple or Google through Convex Auth v2's web flow: the button goes to the
// provider's page, which comes back to this page with a one-time code that ConvexAuthProvider
// redeems as the page loads. Apple and Google are separate accounts, even with the same email
// (packages/convex/convex/users.ts).

const flowMessage = (error: OauthFlowError | null): { title: string; body: string } | null => {
  if (!error) return null;
  switch (error.error) {
    case "REJECTED": {
      const data = error.data as { code?: string; message?: string } | undefined;
      return { title: "Couldn't sign you in", body: data?.message ?? "The server said no." };
    }
    case "ACCESS_DENIED":
      return { title: "Sign-in cancelled", body: "No worries, try again whenever you like." };
    case "EXPIRED":
    case "INVALID_FLOW":
      return { title: "That sign-in timed out", body: "Give it another go." };
    case "OTHER_ERROR":
      return {
        title: "Couldn't sign you in",
        body: "Something went wrong on the way back from the sign-in page. Give it another go.",
      };
  }
};

export const SignIn: React.FC = () => {
  const apple = useSignInWithApple(api.auth);
  const google = useSignInWithGoogle(api.auth);
  const { flowError } = useOauth();
  const [starting, setStarting] = useState<"apple" | "google" | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const message = flowMessage(flowError);

  const start = async (provider: "apple" | "google") => {
    setStarting(provider);
    setStartError(null);
    // Back to this page afterwards. On success the page goes off to Apple or Google, so this only
    // comes back for a failure.
    const result = await (provider == "apple" ? apple : google).signIn({
      redirectTo: window.location.href,
    });
    if (result.status == "error") {
      console.error(result.userError.cause);
      setStartError("Couldn't start signing in. Check your connection and try again.");
      setStarting(null);
    }
  };

  return (
    <Flex direction={"column"} gap={"3"} width={"100%"}>
      {message && (
        <Callout.Root color={flowError?.error == "ACCESS_DENIED" ? "gray" : "red"} role="alert">
          <Callout.Text>
            <Text weight={"bold"}>{message.title}</Text>
            <br />
            {message.body}
          </Callout.Text>
        </Callout.Root>
      )}
      <Button
        size={"3"}
        color={"gray"}
        highContrast
        loading={starting == "apple"}
        disabled={starting != null}
        onClick={() => start("apple")}
      >
        <AppleLogo /> Sign in with Apple
      </Button>
      <Button
        size={"3"}
        variant={"surface"}
        color={"gray"}
        highContrast
        loading={starting == "google"}
        disabled={starting != null}
        onClick={() => start("google")}
      >
        <GoogleLogo /> Sign in with Google
      </Button>
      {startError && (
        <Text size={"2"} color={"red"}>
          {startError}
        </Text>
      )}
      {import.meta.env.DEV && <TestSignIn />}
    </Flex>
  );
};

/**
 * Only on the local dev server: signs in as one of the dev deployment's test accounts without
 * Apple or Google (auth:signInForTesting). The server refuses it on prod anyway.
 */
const TestSignIn: React.FC = () => {
  const signInForTesting = useMutation(api.auth.signInForTesting);
  const { setSession } = useAuthActions();
  const [email, setEmail] = useState("agent@stashit.test");
  const [error, setError] = useState<string | null>(null);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        setError(null);
        signInForTesting({ email })
          .then((result) => setSession(result.tokens))
          .catch((e) => setError(e instanceof Error ? e.message : String(e)));
      }}
    >
      <Flex direction={"column"} gap={"2"} mt={"3"}>
        <Text size={"1"} color={"gray"}>
          Test sign-in (dev only)
        </Text>
        <Flex gap={"2"}>
          <TextField.Root
            type={"email"}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{ flexGrow: 1 }}
          />
          <Button type={"submit"} variant={"soft"}>
            Sign in
          </Button>
        </Flex>
        {error && (
          <Text size={"1"} color={"red"}>
            {error}
          </Text>
        )}
      </Flex>
    </form>
  );
};

const AppleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
    <path
      fill="currentColor"
      d="M16.37 12.6c-.02-2.2 1.8-3.26 1.88-3.31-1.03-1.5-2.62-1.7-3.18-1.73-1.35-.14-2.65.8-3.33.8-.7 0-1.75-.78-2.88-.76-1.48.02-2.85.86-3.61 2.19-1.55 2.68-.4 6.63 1.1 8.8.74 1.06 1.61 2.25 2.75 2.2 1.11-.04 1.52-.71 2.86-.71 1.33 0 1.71.71 2.87.69 1.19-.02 1.94-1.07 2.66-2.14.85-1.23 1.19-2.43 1.21-2.49-.03-.01-2.31-.89-2.33-3.54ZM14.2 6.17c.6-.74 1.01-1.75.9-2.77-.87.04-1.94.59-2.56 1.32-.56.64-1.05 1.69-.92 2.68.97.08 1.97-.49 2.58-1.23Z"
    />
  </svg>
);

const GoogleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
    <path
      fill="#4285F4"
      d="M21.6 12.23c0-.68-.06-1.36-.18-2.02H12v3.83h5.4a4.6 4.6 0 0 1-2 3.02v2.5h3.23c1.9-1.75 2.97-4.32 2.97-7.33Z"
    />
    <path
      fill="#34A853"
      d="M12 22c2.7 0 4.97-.9 6.63-2.43l-3.23-2.5c-.9.6-2.05.95-3.4.95-2.6 0-4.81-1.76-5.6-4.12H3.07v2.58A10 10 0 0 0 12 22Z"
    />
    <path fill="#FBBC04" d="M6.4 13.9a6 6 0 0 1 0-3.8V7.52H3.07a10 10 0 0 0 0 8.96L6.4 13.9Z" />
    <path
      fill="#EA4335"
      d="M12 5.98c1.47-.02 2.88.53 3.95 1.54l2.86-2.86A9.6 9.6 0 0 0 12 2a10 10 0 0 0-8.93 5.52L6.4 10.1C7.19 7.74 9.4 5.98 12 5.98Z"
    />
  </svg>
);
